import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../context/AuthProvider";
import { Button } from "../components/common/Button";
import { Spinner } from "../components/common/Spinner";
import { useLiveWallet } from "../hooks/useLiveWallet";
import { createSubscriptionPlan, quoteSubscriptionPlan, PlanResult } from "../services/subscription.service";
import { callableMessage } from "../services/callables";
import { CartItem, MealType } from "../types";

const SubscriptionPaymentPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Subscription meals are paid from the Ruchira wallet: each meal is debited when the kitchen ticket is
  // created (meal-lock cron), so skipped / cancelled days are never charged. There is no upfront payment.
  const { balance: walletBalance, loaded: walletLoaded } = useLiveWallet(user?.uid);

  const stored = useMemo(() => {
    const raw = sessionStorage.getItem("pendingSubscription");
    return raw ? JSON.parse(raw) : null;
  }, []);
  // Prices are recomputed on the server from the kitchen menus; this is what will be saved.
  const [quote, setQuote] = useState<PlanResult | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const planConfig = useMemo(() => {
    if (!stored) return null;
    return {
      durationDays: stored.duration,
      startDate: new Date(stored.startDate),
      meals: stored.selectedMeals,
      totalAmount: 0,
      lunchTime: stored.lunchTime,
      lunchLocation: stored.lunchLocation ?? undefined,
      status: (stored.startOption === "today" ? "active" : "scheduled") as "active" | "scheduled",
    };
  }, [stored]);

  useEffect(() => {
    if (!stored || !planConfig || !user) return;
    let cancelled = false;
    quoteSubscriptionPlan(planConfig, stored.selections)
      .then((q) => { if (!cancelled) setQuote(q); })
      .catch((e) => { if (!cancelled) setQuoteError(callableMessage(e, "Couldn't check prices with the kitchen menus.")); });
    return () => { cancelled = true; };
  }, [stored, planConfig, user]);

  if (!stored) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-red-500">
          No subscription data found. Please build your plan again.
        </p>
      </div>
    );
  }

  const {
    selections,
    startDate,
    durationDays,
    lunchTime,
    duration,
    selectedMeals,
    startOption,
    lunchLocation,
  } = stored as {
    selections: Record<string, Record<string, CartItem[]>>;
    startDate: string | Date;
    durationDays: number;
    lunchTime?: "8AM" | "12PM";
    duration: 7 | 30;
    selectedMeals: MealType[];
    startOption: "today" | "later";
    lunchLocation: "home" | "office" | null;
  };

  const startDateObj = new Date(startDate);

  const calculateTotalCost = () => {
    let total = 0;
    Object.values(selections).forEach(dayMeals => {
      Object.values(dayMeals).forEach(items => {
        if (items[0]) {
          total += (Number(items[0].price) || 0) * (Number(items[0].quantity) || 1);
        }
      });
    });
    return total;
  };

  const clientTotal = calculateTotalCost();
  const totalAmount = quote ? quote.totalAmount : clientTotal;

  const formatDisplayDate = (date: Date) =>
    date.toLocaleDateString("en-IN", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const handleConfirm = async () => {
    if (!user) {
      toast.error("Please login to subscribe");
      return;
    }

    if (!quote || quote.days === 0) {
      toast.error(quoteError || "None of the selected meals are available. Please go back and pick again.");
      return;
    }

    if (loading) return;
    await createPlan();
  };

  const createPlan = async () => {
    setLoading(true);
    try {
      const res = await createSubscriptionPlan(planConfig!, selections);
      const subscriptionStatus = res.status;

      toast.success(
        subscriptionStatus === "scheduled"
          ? `Subscription scheduled! Will start on ${formatDisplayDate(new Date(res.startDate + "T00:00:00"))}`
          : "Subscription Active! Your calendar is set."
      );
      if (res.dropped?.length) {
        toast(`${res.dropped.length} item(s) were left out because the kitchen no longer offers them.`, { icon: "ℹ️" });
      }

      sessionStorage.removeItem("pendingSubscription");
      navigate("/my-tiffin");
    } catch (err) {
      console.error(err);
      toast.error(callableMessage(err, "Could not create your subscription. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Spinner size={48} />
        <p className="mt-4 text-gray-500 animate-pulse">
          Creating your subscription...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-md mx-auto px-4 py-8 space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Confirm your subscription
        </h1>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Duration</span>
            <span className="font-semibold">{durationDays} days</span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">Start date</span>
            <span className="font-semibold">
              {formatDisplayDate(startDateObj)}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">Meals</span>
            <span className="font-semibold capitalize">
              {selectedMeals.join(", ").replaceAll("_", " ")}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">Total amount</span>
            <span className="font-bold text-lg text-green-700">
              {quote ? `₹${totalAmount.toFixed(2)}` : quoteError ? "—" : "Checking prices…"}
            </span>
          </div>
          {quote && Math.abs(quote.totalAmount - clientTotal) >= 0.01 && (
            <p className="text-xs text-gray-500">
              Updated to the kitchens' current menu prices (was ₹{clientTotal.toFixed(2)}).
            </p>
          )}
        </div>

        {quoteError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{quoteError}</div>
        )}

        {quote && quote.dropped.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-gray-700 space-y-1">
            <p className="font-medium">These picks are no longer on the menu and will be left out:</p>
            <ul className="list-disc pl-5 text-xs">
              {quote.dropped.slice(0, 8).map((d, i) => (
                <li key={i}>
                  {d.date} · {String(d.meal).replace("_", " ")}{d.backup ? " (backup)" : ""}: {d.name} — {d.reason}
                </li>
              ))}
              {quote.dropped.length > 8 && <li>…and {quote.dropped.length - 8} more</li>}
            </ul>
          </div>
        )}

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-gray-700 space-y-1">
          <p>
            Meals are paid from your <strong>Ruchira wallet</strong>. Each meal is charged at the kitchen's menu
            price when the kitchen starts it, so days you skip or cancel are never charged.
          </p>
          <p>
            Your meals are never held back for a low balance: if the wallet runs short, it can go below ₹0.
            Please top it up to bring it back above zero.
          </p>
          {walletLoaded && (
            <p>
              Wallet balance:{" "}
              <strong className={walletBalance < 0 ? "text-red-600" : undefined}>
                {walletBalance < 0 ? "−" : ""}₹{Math.abs(walletBalance).toFixed(2)}
              </strong>
              {walletBalance < totalAmount && (
                <span className="text-red-600">
                  {" "}· add ₹{(totalAmount - walletBalance).toFixed(2)} to cover the whole plan.{" "}
                  <button className="underline font-medium" onClick={() => navigate("/profile")}>Add money</button>
                </span>
              )}
            </p>
          )}
        </div>

        <Button
          className="w-full py-3 text-lg shadow-lg shadow-orange-100"
          onClick={handleConfirm}
          disabled={loading || !quote || quote.days === 0}
        >
          Confirm subscription
        </Button>

        <button
          onClick={() => navigate(-1)}
          className="w-full text-xs text-gray-500 mt-2 underline"
        >
          ← Back to planner
        </button>
      </div>

    </div>
  );
};

export default SubscriptionPaymentPage;
